import { Mail, Phone, User } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import FieldAvatar from './FieldAvatar'
import { mailtoHref, telHref } from 'utils/contact'

export interface FieldPartnerSheetProps {
	/** The partner to show. Null closes the sheet. */
	partner: any | null
	onClose: () => void
}

/**
 * One partner, in full.
 *
 * **Read-only, deliberately.** A partner's name, title, company, email and
 * phone live on THEIR user record, so editing them here would rewrite what
 * every other user in the system sees and overwrite what that person set about
 * themselves. The desktop `PartnerProfileDialog` takes the same line and carries no
 * inputs either.
 *
 * What it adds over the row behind it is room: the list truncates a title and a
 * company onto one line and cannot show an email at all, which is the detail
 * somebody actually wants when they are about to write to a broker.
 */
export function FieldPartnerSheet({ partner, onClose }: FieldPartnerSheetProps) {
	if (!partner) return null

	const person = partner.partner || {}
	const name = person.name || person.firstName || 'Unnamed partner'
	const subtitle = [person.title, person.company].filter(Boolean).join(', ')
	const mailto = mailtoHref(person.email)
	const tel = telHref(person.phone)

	return (
		<Dialog open onClose={onClose}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>{name}</DialogTitle>
					<DialogDescription>{subtitle || 'Partner'}</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 p-5 pt-2">
					{/* The header above already carries the name and the title, so
					    repeating them here would just be saying it twice. */}
					<div className="flex justify-center">
						<FieldAvatar
							src={person.image}
							name={name}
							fallbackIcon={User}
							label={name}
							className="h-20 w-20"
						/>
					</div>

					{/*
						Shown as text as well as offered as an action. A rep reading a
						number aloud to somebody else cannot do that from a button, and
						the list has no room for it at all.
					*/}
					<dl className="space-y-2 text-sm">
						{person.email && (
							<div>
								<dt className="text-xs font-medium text-gray-500">Email</dt>
								<dd className="break-all text-gray-900">{person.email}</dd>
							</div>
						)}
						{person.phone && (
							<div>
								<dt className="text-xs font-medium text-gray-500">Phone</dt>
								<dd className="text-gray-900">{person.phone}</dd>
							</div>
						)}
						{!person.email && !person.phone && (
							<p className="text-sm text-gray-500">
								No contact details on this partner yet. They are filled in from that
								person&rsquo;s own profile.
							</p>
						)}
					</dl>

					{/*
						Anchors with real hrefs, so the long-press menu offers Add to
						Contacts and Copy. An action with nothing behind it is absent
						rather than dead, the same rule as the list.
					*/}
					{(mailto || tel) && (
						<div className="flex gap-2">
							{mailto && (
								<a
									href={mailto}
									aria-label={`Email ${name}`}
									className="inline-flex min-h-[2.75rem] flex-1 items-center justify-center gap-2 rounded-md border border-gray-300 text-sm font-medium text-primary"
								>
									<Mail className="h-4 w-4" />
									Email
								</a>
							)}
							{tel && (
								<a
									href={tel}
									aria-label={`Call ${name}`}
									className="inline-flex min-h-[2.75rem] flex-1 items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-white"
								>
									<Phone className="h-4 w-4" />
									Call
								</a>
							)}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default FieldPartnerSheet
